import React, { useContext, useEffect, useMemo, useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/functions";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { db } from "../../../firebase";
import { stripUndefinedValues } from "../../../utils/firestoreData";
import {
  getCommerceItems,
  getTicketAvailability,
  isCommerceItemAvailabilityHidden,
} from "../../../utils/ticketConfiguration";
import { AdditionalFieldHtml } from "../../AdditionalFieldHtml";
import { CurrentEventDataContext } from "../CurrentEventDataContext";
import { TicketConfigurationSummary } from "./TicketConfigurationSummary";

const normalizeCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
};

const Tickets = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setCurrentEventData } = useContext(CurrentEventDataContext);
  const [eventData, setEventData] = useState(null);
  const [eventSoldCount, setEventSoldCount] = useState(0);
  const [soldCounts, setSoldCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let active = true;

    const loadTickets = async () => {
      if (!id) {
        navigate("/manage");
        return;
      }

      try {
        const snapshot = await db.collection("events").doc(id).get();
        const eventDocument = snapshot.data();
        const loadedEvent = eventDocument
          ? { ...eventDocument, id: eventDocument.id || id }
          : null;

        if (!loadedEvent) {
          throw new Error("Event not found.");
        }

        const tickets = getCommerceItems(loadedEvent);
        const regCountRequest = firebase
          .functions()
          .httpsCallable("appV2/regCount")({ id });
        const itemCountRequest = firebase.functions().httpsCallable("appV2/regItemCount");
        const [registrationResult, ...itemResults] = await Promise.all([
          regCountRequest,
          ...tickets.map((ticket) =>
            itemCountRequest({ id, fieldName: ticket.fieldName }),
          ),
        ]);

        if (!active) return;

        const nextSoldCounts = tickets.reduce((counts, ticket, index) => {
          counts[ticket.fieldName] = normalizeCount(itemResults[index]?.data);
          return counts;
        }, {});

        setEventData(loadedEvent);
        setCurrentEventData(loadedEvent);
        sessionStorage.setItem("eventData", JSON.stringify(loadedEvent));
        setEventSoldCount(normalizeCount(registrationResult?.data));
        setSoldCounts(nextSoldCounts);
      } catch (error) {
        toast.error(error.message || "Unable to load ticket capacity.");
      } finally {
        if (active) setIsLoading(false);
      }
    };

    loadTickets();

    return () => {
      active = false;
    };
  }, [id, navigate, setCurrentEventData]);

  const tickets = useMemo(
    () => getCommerceItems(eventData),
    [eventData],
  );

  const updateTicket = (fieldName, propertyName, value) => {
    setEventData((currentEvent) => ({
      ...currentEvent,
      additionalFields: (currentEvent?.additionalFields || []).map((field) => {
        if (field.fieldName !== fieldName) return field;

        const nextField = { ...field, [propertyName]: value };
        if (propertyName === "fieldCapacity" && value !== "itemCapacity") {
          delete nextField.fieldCapacityNum;
        }
        return nextField;
      }),
    }));
  };

  const handleSave = async () => {
    if (!eventData?.id) return;

    const invalidTicket = tickets.find(
      (ticket) =>
        ticket.fieldCapacity === "itemCapacity" &&
        (ticket.fieldCapacityNum === "" ||
          ticket.fieldCapacityNum == null ||
          !Number.isFinite(Number(ticket.fieldCapacityNum)) ||
          Number(ticket.fieldCapacityNum) < 0),
    );

    if (invalidTicket) {
      toast.error("Every item-capacity ticket requires a capacity of zero or more.");
      return;
    }

    setIsSaving(true);
    try {
      const nextEvent = stripUndefinedValues({
        ...eventData,
        updatedAt: new Date(),
      });
      await db.collection("events").doc(eventData.id).update({
        additionalFields: nextEvent.additionalFields || [],
        updatedAt: nextEvent.updatedAt,
      });
      setEventData(nextEvent);
      setCurrentEventData(nextEvent);
      sessionStorage.setItem("eventData", JSON.stringify(nextEvent));
      toast.success("Ticket capacity saved.");
    } catch (error) {
      toast.error(error.message || "Unable to save ticket capacity.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <p className="text-sm text-zinc-600 dark:text-zinc-300">Loading tickets…</p>;
  }

  if (!eventData) {
    return (
      <div>
        <h1 className="text-2xl font-semibold">Tickets</h1>
        <p>Ticket configuration could not be loaded.</p>
      </div>
    );
  }

  const primaryBtnCls =
    "inline-flex min-h-11 items-center justify-center rounded-md bg-zinc-900 px-4 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100";
  const secondaryBtnCls =
    "inline-flex min-h-11 items-center justify-center rounded-md bg-zinc-50 px-4 py-2 text-sm font-semibold text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:hover:bg-zinc-700";
  const inputCls =
    "mt-2 block w-full rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:focus:ring-white";

  return (
    <div className="space-y-6 pb-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="mb-1 text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
            Tickets
          </h1>
          <p className="mb-0 text-sm text-zinc-600 dark:text-zinc-300">
            Configure ticket capacity and review current sales for {eventData.name || eventData.sysname}.
          </p>
        </div>
        <div className="rounded-md bg-zinc-100 px-3 py-2 text-sm text-zinc-700 ring-1 ring-inset ring-zinc-200 dark:bg-zinc-900 dark:text-zinc-300 dark:ring-zinc-700">
          Current event registrations: <strong>{eventSoldCount}</strong>
        </div>
      </header>

      <TicketConfigurationSummary
        eventData={eventData}
        eventSoldCount={eventSoldCount}
        soldCounts={soldCounts}
      />

      <section className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-900">
        <h2 className="mb-1 text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Ticket capacity
        </h2>
        <p className="mb-0 text-sm text-zinc-600 dark:text-zinc-300">
          Other event, quantity, conditional-visibility, and payment rules still apply.
        </p>

        {tickets.length ? (
          <div className="mt-4 space-y-3">
            {tickets.map((ticket, index) => {
              const availability = getTicketAvailability(
                ticket,
                soldCounts[ticket.fieldName],
                eventData,
                eventSoldCount,
              );

              return (
                <article
                  className="grid gap-4 rounded-md border border-zinc-200 p-4 md:grid-cols-[minmax(0,2fr)_minmax(12rem,1fr)_minmax(10rem,1fr)] dark:border-zinc-700"
                  key={ticket.fieldName || index}
                >
                  <div className="min-w-0">
                    <h3 className="mb-1 font-semibold text-zinc-900 dark:text-zinc-100">
                      <AdditionalFieldHtml html={ticket.fieldLabel || `Ticket ${index + 1}`} />
                    </h3>
                    <code className="break-all text-xs text-zinc-500 dark:text-zinc-400">
                      {ticket.fieldName || "Name not configured"}
                    </code>
                    <p className="mb-0 mt-2 text-sm text-zinc-600 dark:text-zinc-300">
                      Sold: <strong>{availability.sold}</strong> · Remaining:{" "}
                      <strong>
                        {availability.remaining == null
                          ? availability.scope === "unlimited"
                            ? "Not limited"
                            : "Not configured"
                          : availability.remaining}
                      </strong>
                    </p>
                  </div>

                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    Capacity rule
                    <select
                      className={inputCls}
                      onChange={(event) =>
                        updateTicket(
                          ticket.fieldName,
                          "fieldCapacity",
                          event.target.value,
                        )
                      }
                      value={ticket.fieldCapacity || ""}
                    >
                      <option value="">No capacity limit</option>
                      <option value="eventCapacity">Use event capacity</option>
                      <option value="itemCapacity">Ticket capacity</option>
                    </select>
                  </label>

                  {ticket.fieldCapacity === "itemCapacity" ? (
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                      Total ticket capacity
                      <input
                        className={inputCls}
                        min="0"
                        onChange={(event) =>
                          updateTicket(
                            ticket.fieldName,
                            "fieldCapacityNum",
                            event.target.value,
                          )
                        }
                        type="number"
                        value={ticket.fieldCapacityNum ?? ""}
                      />
                    </label>
                  ) : (
                    <div className="text-sm text-zinc-500 dark:text-zinc-400">
                      {ticket.fieldCapacity === "eventCapacity"
                        ? `Shared event capacity: ${eventData.capacity ?? "not configured"}`
                        : "This ticket has no shared capacity limit."}
                    </div>
                  )}
                  <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 md:col-span-3">
                    <input
                      checked={ticket.fieldCheckIn || false}
                      className="h-4 w-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white dark:focus:ring-white"
                      onChange={(event) =>
                        updateTicket(
                          ticket.fieldName,
                          "fieldCheckIn",
                          event.target.checked,
                        )
                      }
                      type="checkbox"
                    />
                    Allow each purchased unit to be checked in individually
                  </label>
                  <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 md:col-span-3">
                    <input
                      checked={isCommerceItemAvailabilityHidden(ticket)}
                      className="h-4 w-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white dark:focus:ring-white"
                      onChange={(event) =>
                        updateTicket(
                          ticket.fieldName,
                          "hideAvailableQuantity",
                          event.target.checked,
                        )
                      }
                      type="checkbox"
                    />
                    Hide available quantity on the registration form
                  </label>
                </article>
              );
            })}
          </div>
        ) : (
          <p className="mb-0 mt-4 text-sm text-zinc-600 dark:text-zinc-300">
            No Commerce Item fields are configured in Event Setup.
          </p>
        )}
      </section>

      <div className="flex flex-wrap gap-3">
        <button
          className={primaryBtnCls}
          disabled={isSaving}
          onClick={handleSave}
          type="button"
        >
          {isSaving ? "Saving…" : "Save ticket capacity"}
        </button>
        <button
          className={secondaryBtnCls}
          onClick={() => navigate(`/manage/event/${eventData.id}`)}
          type="button"
        >
          Back to Event Setup
        </button>
      </div>
    </div>
  );
};

export default Tickets;
